export interface Club {
  id: string;
  name: string;
  sport: string;
  location: string;
  description: string;
  memberCount: number;
  foundedAt: string;
  isActive: boolean;
  contactInfo: {
    email?: string;
    phone?: string;
    website?: string;
  };
  /** Emails of invited members (for clubs created with just emails) */
  invitedEmails?: string[];
}

export interface ClubMember {
  userId: string;
  displayName: string;
  ranking: number;
  joinedAt: string;
  isActive: boolean;
  skillLevel: 'beginner' | 'intermediate' | 'advanced';
  playingStyle: 'power' | 'control' | 'all-around';
}

class ClubService {
  private readonly STORAGE_KEYS = {
    clubs: 'mysportscoach_clubs',
    clubMembers: 'mysportscoach_club_members',
  };

  constructor() {
    this.initializeSampleClubs();
  }

  private initializeSampleClubs() {
    const storedClubs = this.getStoredData<Club[]>(this.STORAGE_KEYS.clubs);
    if (storedClubs.length === 0) {
      const sampleClubs: Club[] = [
        {
          id: 'austin-pickleball-club',
          name: 'Austin Pickleball Club',
          sport: 'pickleball',
          location: 'Austin, TX',
          description: 'Premier pickleball club in Austin with multiple courts and regular tournaments.',
          memberCount: 45,
          foundedAt: '2020-01-15',
          isActive: true,
          contactInfo: {
            email: 'info@austinpickleball.com',
            website: 'austinpickleball.com'
          }
        },
        {
          id: 'round-rock-rec-center',
          name: 'Round Rock Rec Center',
          sport: 'pickleball',
          location: 'Round Rock, TX',
          description: 'Community recreation center with pickleball courts and beginner-friendly programs.',
          memberCount: 28,
          foundedAt: '2019-06-01',
          isActive: true,
          contactInfo: {
            email: 'rec@roundrocktx.gov',
            phone: '(512) 218-5540'
          }
        },
        {
          id: 'pflugerville-community-courts',
          name: 'Pflugerville Community Courts',
          sport: 'pickleball',
          location: 'Pflugerville, TX',
          description: 'Community courts with regular play sessions and skill development programs.',
          memberCount: 32,
          foundedAt: '2021-03-10',
          isActive: true,
          contactInfo: {
            email: 'community@pflugerville.com'
          }
        },
        {
          id: 'cedar-park-recreation',
          name: 'Cedar Park Recreation',
          sport: 'pickleball',
          location: 'Cedar Park, TX',
          description: 'Recreation center offering pickleball leagues and open play sessions.',
          memberCount: 38,
          foundedAt: '2020-09-20',
          isActive: true,
          contactInfo: {
            email: 'recreation@cedarpark.com',
            phone: '(512) 401-5000'
          }
        },
        {
          id: 'austin-tennis-club',
          name: 'Austin Tennis Club',
          sport: 'tennis',
          location: 'Austin, TX',
          description: 'Premier tennis club with professional coaching and tournament facilities.',
          memberCount: 120,
          foundedAt: '2018-04-15',
          isActive: true,
          contactInfo: {
            email: 'info@austintennisclub.com',
            website: 'austintennisclub.com'
          }
        },
        {
          id: 'westlake-country-club',
          name: 'Westlake Country Club',
          sport: 'golf',
          location: 'Westlake, TX',
          description: 'Exclusive golf club with championship course and professional instruction.',
          memberCount: 85,
          foundedAt: '2015-08-01',
          isActive: true,
          contactInfo: {
            email: 'membership@westlakecc.com',
            website: 'westlakecc.com'
          }
        }
      ];
      this.setStoredData(this.STORAGE_KEYS.clubs, sampleClubs);
    }

    // Initialize sample club members
    this.initializeSampleClubMembers();
  }

  private initializeSampleClubMembers() {
    const storedMembers = this.getStoredData<{ [clubId: string]: ClubMember[] }>(this.STORAGE_KEYS.clubMembers);
    if (Object.keys(storedMembers).length === 0) {
      const sampleMembers: { [clubId: string]: ClubMember[] } = {
        'austin-pickleball-club': [
          {
            userId: 'player1',
            displayName: 'Alice Smith',
            ranking: 1,
            joinedAt: '2023-01-15',
            isActive: true,
            skillLevel: 'advanced',
            playingStyle: 'control'
          },
          {
            userId: 'player2',
            displayName: 'Bob Johnson',
            ranking: 2,
            joinedAt: '2023-02-20',
            isActive: true,
            skillLevel: 'advanced',
            playingStyle: 'power'
          },
          {
            userId: 'default-user',
            displayName: 'Demo User',
            ranking: 15,
            joinedAt: '2023-06-01',
            isActive: true,
            skillLevel: 'intermediate',
            playingStyle: 'all-around'
          },
          {
            userId: 'player4',
            displayName: 'David Green',
            ranking: 3,
            joinedAt: '2023-03-10',
            isActive: true,
            skillLevel: 'intermediate',
            playingStyle: 'power'
          },
          {
            userId: 'player8',
            displayName: 'Henry Kim',
            ranking: 4,
            joinedAt: '2023-04-05',
            isActive: true,
            skillLevel: 'advanced',
            playingStyle: 'power'
          }
        ],
        'round-rock-rec-center': [
          {
            userId: 'player3',
            displayName: 'Carol White',
            ranking: 1,
            joinedAt: '2023-05-15',
            isActive: true,
            skillLevel: 'beginner',
            playingStyle: 'all-around'
          },
          {
            userId: 'player6',
            displayName: 'Frank Brown',
            ranking: 2,
            joinedAt: '2023-07-20',
            isActive: true,
            skillLevel: 'beginner',
            playingStyle: 'power'
          }
        ],
        'pflugerville-community-courts': [
          {
            userId: 'player5',
            displayName: 'Eve Black',
            ranking: 1,
            joinedAt: '2023-06-10',
            isActive: true,
            skillLevel: 'advanced',
            playingStyle: 'control'
          }
        ],
        'cedar-park-recreation': [
          {
            userId: 'player7',
            displayName: 'Grace Lee',
            ranking: 1,
            joinedAt: '2023-08-01',
            isActive: true,
            skillLevel: 'intermediate',
            playingStyle: 'all-around'
          }
        ]
      };
      this.setStoredData(this.STORAGE_KEYS.clubMembers, sampleMembers);
    }
  }

  // Club Management
  async getClubsBySport(sportId: string): Promise<Club[]> {
    const clubs = this.getStoredData<Club[]>(this.STORAGE_KEYS.clubs);
    return clubs.filter(club => club.sport === sportId && club.isActive);
  }

  async getClubById(clubId: string): Promise<Club | null> {
    const clubs = this.getStoredData<Club[]>(this.STORAGE_KEYS.clubs);
    return clubs.find(club => club.id === clubId) || null;
  }

  async createClub(club: Omit<Club, 'id' | 'foundedAt' | 'memberCount'>): Promise<string> {
    const clubs = this.getStoredData<Club[]>(this.STORAGE_KEYS.clubs);
    const newClub: Club = {
      ...club,
      id: `club_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      foundedAt: new Date().toISOString(),
      memberCount: 0
    };
    
    clubs.push(newClub);
    this.setStoredData(this.STORAGE_KEYS.clubs, clubs);
    return newClub.id;
  }

  // Club Membership Management
  async getClubMembers(clubId: string): Promise<ClubMember[]> {
    const allMembers = this.getStoredData<{ [clubId: string]: ClubMember[] }>(this.STORAGE_KEYS.clubMembers);
    return allMembers[clubId] || [];
  }

  async addClubMember(clubId: string, member: Omit<ClubMember, 'joinedAt'>): Promise<void> {
    const allMembers = this.getStoredData<{ [clubId: string]: ClubMember[] }>(this.STORAGE_KEYS.clubMembers);
    
    if (!allMembers[clubId]) {
      allMembers[clubId] = [];
    }

    const newMember: ClubMember = {
      ...member,
      joinedAt: new Date().toISOString()
    };

    allMembers[clubId].push(newMember);
    
    // Update club member count
    const clubs = this.getStoredData<Club[]>(this.STORAGE_KEYS.clubs);
    const clubIndex = clubs.findIndex(club => club.id === clubId);
    if (clubIndex !== -1) {
      clubs[clubIndex].memberCount = allMembers[clubId].length;
      this.setStoredData(this.STORAGE_KEYS.clubs, clubs);
    }

    this.setStoredData(this.STORAGE_KEYS.clubMembers, allMembers);
  }

  async removeClubMember(clubId: string, userId: string): Promise<void> {
    const allMembers = this.getStoredData<{ [clubId: string]: ClubMember[] }>(this.STORAGE_KEYS.clubMembers);
    
    if (allMembers[clubId]) {
      allMembers[clubId] = allMembers[clubId].filter(member => member.userId !== userId);
      
      // Update club member count
      const clubs = this.getStoredData<Club[]>(this.STORAGE_KEYS.clubs);
      const clubIndex = clubs.findIndex(club => club.id === clubId);
      if (clubIndex !== -1) {
        clubs[clubIndex].memberCount = allMembers[clubId].length;
        this.setStoredData(this.STORAGE_KEYS.clubs, clubs);
      }

      this.setStoredData(this.STORAGE_KEYS.clubMembers, allMembers);
    }
  }

  async updateMemberRanking(clubId: string, userId: string, newRanking: number): Promise<void> {
    const allMembers = this.getStoredData<{ [clubId: string]: ClubMember[] }>(this.STORAGE_KEYS.clubMembers);
    
    if (allMembers[clubId]) {
      const memberIndex = allMembers[clubId].findIndex(member => member.userId === userId);
      if (memberIndex !== -1) {
        allMembers[clubId][memberIndex].ranking = newRanking;
        this.setStoredData(this.STORAGE_KEYS.clubMembers, allMembers);
      }
    }
  }

  async getClubRankings(clubId: string): Promise<ClubMember[]> {
    const members = await this.getClubMembers(clubId);
    return members
      .filter(member => member.isActive)
      .sort((a, b) => a.ranking - b.ranking);
  }

  // Helper methods for localStorage
  private getStoredData<T>(key: string): T {
    try {
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : (Array.isArray(new (Object as any)()) ? [] : {});
    } catch (error) {
      console.error(`Error reading ${key} from localStorage:`, error);
      return Array.isArray(new (Object as any)()) ? [] : {} as T;
    }
  }

  private setStoredData<T>(key: string, data: T): void {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error(`Error writing ${key} to localStorage:`, error);
    }
  }
}

export const clubService = new ClubService();
export default clubService;
